import { Button, FlexBox } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { StatsTheme } from '../../types'
import { ReaderSummaryShareCard } from './ReaderSummaryShareCard'
import { analytics } from 'segment_analytics'
import { useStats } from '../../lib/ctx/stats'
import { getBackgroundGradient } from '../../lib/utils'
import { sendToNative } from 'app/webview/utils'

interface Props {
  theme: StatsTheme
  summary: string
  loading: boolean
  canShareStats: boolean
  canEditGoal: boolean
}

export const ReaderSummaryShareButtons = ({
  theme,
  summary,
  loading,
  canShareStats,
  canEditGoal,
}: Props) => {
  const { colors } = useTheme()
  const [openShareModal, goal, isV2] = useStats((s) => [
    s.openShareModal,
    s.stats?.reading_goal,
    s.isV2,
  ])
  const showEditGoalButton = canEditGoal && isV2
  const canEditGoalOrShare = canShareStats !== showEditGoalButton

  const handleEditGoal = () => {
    if (!goal?.id) return

    sendToNative({
      id: 'edit-reading-goal',
      payload: {
        type: 'deep_link',
        body: {
          url: `https://fable.co/app/reading_goals_edit?goal_id=${goal.id}`,
          close: true,
        },
      },
    })
  }

  const handleCreateImage = () => {
    openShareModal({
      component: <ReaderSummaryShareCard theme={theme} summary={summary} />,
      type: 'reader-summary',
      width: 392,
      backgroundColor: theme.primary,
      gradient: getBackgroundGradient(theme.key),
      retry: 2,
    })

    analytics.events.stats.statsShareTap({ type: 'hero' })
  }

  if (!canShareStats && !showEditGoalButton) return null

  return (
    <FlexBox
      className={css`
        width: 100%;
        gap: 8px;
        pointer-events: ${loading ? 'none' : 'auto'};
        opacity: ${loading ? 0.5 : 1};
      `}
      centerAll
    >
      {showEditGoalButton ? (
        <Button
          dataTestId="stats-edit-goal-button-reader-summary"
          className={css`
            width: 100%;
            max-width: ${canEditGoalOrShare ? '152px' : 'none'};
            height: 52px;
            color: ${colors.uiWhite};
            border-color: ${colors.uiWhite};
            &:hover {
              color: ${colors.uiWhite};
              border-color: ${colors.uiWhite};
            }
          `}
          onClick={handleEditGoal}
          negative
          outline
        >
          Edit goal
        </Button>
      ) : null}
      {canShareStats ? (
        <Button
          dataTestId="stats-share-button-reader-summary"
          className={css`
            color: ${theme.accent};
            width: 100%;
            max-width: ${canEditGoalOrShare ? '152px' : 'none'};
            height: 52px;
            &:hover {
              color: ${theme.accent};
            }
          `}
          onClick={handleCreateImage}
          negative
        >
          Share
        </Button>
      ) : null}
    </FlexBox>
  )
}
